import { useState, useEffect, useRef } from 'react';
import { Row, Column, Button } from '@bancobarigui/barihaus';
import { DatePicker, message, Select } from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import { useAuth } from '@/contexts/auth.context';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { BankRoles, ProviderRoles } from '@/shared/enums/UserRoles';
import { providerCompanyService } from '@/services/providerCompany.service';
import { inspectionTypeService } from '@/services/inspectionType.service';
import dayjs, { Dayjs } from 'dayjs';
import locale from 'antd/es/date-picker/locale/pt_BR';
import 'dayjs/locale/pt-br';

export interface ReportsFilterValues {
  startDate?: string;
  endDate?: string;
  providerId?: string;
  operationalProviderId?: string;
  inspectionTypeIds?: string[];
}

interface ReportsFiltersProps {
  onFilterChange: (filters: ReportsFilterValues) => void;
  onClear: () => void;
  disabled?: boolean;
}

export function ReportsFilters({ onFilterChange, onClear, disabled = false }: ReportsFiltersProps) {
  const { user } = useAuth();
  const { hasRole, isProviderUser, isInspectorUser } = usePermissions();

  
  dayjs.locale('pt-br');

  
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestVersionRef = useRef(0);

  
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>([null, null]);
  const [selectedProviderId, setSelectedProviderId] = useState<string | undefined>();
  const [selectedOperationalProviderId, setSelectedOperationalProviderId] = useState<string | undefined>();
  const [selectedInspectionType, setSelectedInspectionType] = useState<string | undefined>();


  const [providers, setProviders] = useState<Array<{ value: string; label: string }>>([]);
  const [operationalProviders, setOperationalProviders] = useState<Array<{ value: string; label: string }>>([]);
  const [inspectionTypes, setInspectionTypes] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingOperationalProviders, setLoadingOperationalProviders] = useState(false);
  const [loadingInspectionTypes, setLoadingInspectionTypes] = useState(false);

  
  
  const canSeeProviderFilter = hasRole([
    BankRoles.ADMIN_BANK,
    BankRoles.BANK_OPERATIONAL,
    BankRoles.ANTI_FRAUD,
    BankRoles.ENG_MANAGEMENT,
    BankRoles.CONSULTANT
  ]);

  
  const canSeeOperationalProviderFilter = hasRole([
    BankRoles.ADMIN_BANK,
    ProviderRoles.MASTER_PROVIDER
  ]);

  useEffect(() => {
    if (canSeeProviderFilter) {
      loadProviders();
    }
    loadInspectionTypes();
  }, [canSeeProviderFilter]);

  useEffect(() => {
    if (canSeeOperationalProviderFilter) {
      requestVersionRef.current += 1;
      const currentVersion = requestVersionRef.current;

      setOperationalProviders([]);
      setSelectedOperationalProviderId(undefined);

      loadOperationalProviders(currentVersion);
    } else {
      setOperationalProviders([]);
      setSelectedOperationalProviderId(undefined);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [canSeeOperationalProviderFilter, selectedProviderId]);


  const loadProviders = async () => {
    try {
      setLoadingProviders(true);
      const response = await providerCompanyService.getAllProviderCompanies();
      const mappedProviders = response.map(p => ({ value: p.id, label: p.name }));
      setProviders(mappedProviders);
    } catch {
      message.error('Erro ao carregar lista de prestadoras');
    } finally {
      setLoadingProviders(false);
    }
  };

  const loadInspectionTypes = async () => {
    try {
      setLoadingInspectionTypes(true);
      const response = await inspectionTypeService.getAllInspectionTypes();
      const mappedTypes = response.map(t => ({ value: t.id, label: t.name }));
      setInspectionTypes(mappedTypes);
    } catch {
      message.error('Erro ao carregar lista de tipos de inspeção');
    } finally {
      setLoadingInspectionTypes(false);
    }
  };

  const loadOperationalProviders = async (expectedVersion: number) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    try {
      setLoadingOperationalProviders(true);

      if (hasRole([BankRoles.ADMIN_BANK]) && !selectedProviderId) {
        return;
      }

      const masterProviderId = isProviderUser() ? user?.providerId : selectedProviderId;

      if (!masterProviderId) {
        return;
      }

      const response = await providerCompanyService.getOperationalProviders(masterProviderId);

      if (currentController.signal.aborted) {
        return;
      }

      if (requestVersionRef.current !== expectedVersion) {
        return;
      }

      const mappedOperationals = response.map(p => ({
        value: `${p.id}|${p.name}`,
        label: p.name,
        originalId: p.id
      }));

      const uniqueOperationals = Array.from(
        new Map(mappedOperationals.map(item => [item.value, item])).values()
      );

      if (requestVersionRef.current !== expectedVersion) {
        return;
      }

      setOperationalProviders(uniqueOperationals);

      if (response.length === 0) {
        message.info('Nenhuma operacional encontrada para esta master');
      }
    } catch (error: any) {
      if (error.name === 'AbortError' || currentController.signal.aborted) {
        return;
      }

      message.error('Erro ao carregar lista de operacionais');
      setOperationalProviders([]);
    } finally {
      if (!currentController.signal.aborted) {
        setLoadingOperationalProviders(false);
      }
    }
  };


  
  const extractIdFromCompositeKey = (compositeKey: string | undefined): string | undefined => {
    if (!compositeKey) return undefined;
    return compositeKey.split('|')[0];
  };

  
  const validateDateRange = (range: [Dayjs | null, Dayjs | null] | null): boolean => {
    
    if (!range || !range[0] || !range[1]) return true;

    const diffDays = range[1].diff(range[0], 'day');

    if (diffDays < 0) {
      message.error('A data final deve ser maior que a data inicial');
      return false;
    }

    if (diffDays > 365) {
      message.warning('Período máximo permitido é de 365 dias');
      return false;
    }

    return true;
  };

  const handleFilter = () => {
    if (!validateDateRange(dateRange)) {
      return;
    }

    const extractedOperationalId = extractIdFromCompositeKey(selectedOperationalProviderId);

    const filters: ReportsFilterValues = {
      startDate: dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
      endDate: dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      providerId: selectedProviderId || undefined,


      operationalProviderId: extractedOperationalId || undefined,
      inspectionTypeIds: selectedInspectionType ? [selectedInspectionType] : undefined,
    };

    onFilterChange(filters);
    message.success('Filtros aplicados com sucesso');
  };


  const handleClear = () => {
    setDateRange([null, null]);
    setSelectedProviderId(undefined);
    setSelectedOperationalProviderId(undefined);
    setSelectedInspectionType(undefined);
    onClear();
    message.info('Filtros limpos');
  };

  
  const datePresets = [
    { label: 'Últimos 7 dias', value: [dayjs().subtract(7, 'day'), dayjs()] as [Dayjs, Dayjs] },
    { label: 'Últimos 30 dias', value: [dayjs().subtract(30, 'day'), dayjs()] as [Dayjs, Dayjs] },
    { label: 'Últimos 90 dias', value: [dayjs().subtract(90, 'day'), dayjs()] as [Dayjs, Dayjs] },
    { label: 'Mês atual', value: [dayjs().startOf('month'), dayjs().endOf('month')] as [Dayjs, Dayjs] },
    { label: 'Ano atual', value: [dayjs().startOf('year'), dayjs().endOf('year')] as [Dayjs, Dayjs] },
  ];

  return (
    <div
      style={{
        background: '#ffffff',
        padding: '24px',
        borderRadius: '8px',
        marginBottom: '24px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
        border: '1px solid #f0f0f0',
      }}
    >
      <Row gutter={[16, 16]}>
        {}
        <Column xs={24} sm={12} lg={6}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
            Período
          </label>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format="DD/MM/YYYY"
            placeholder={['Data inicial', 'Data final']}
            style={{ width: '100%' }}
            presets={datePresets}
            disabled={disabled}
            locale={locale}
          />
        </Column>

        {}
        {canSeeProviderFilter && (
          <Column xs={24} sm={12} lg={6}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              Prestadora Master
            </label>
            <Select
              value={selectedProviderId}
              onChange={(value) => {
                setSelectedProviderId(value);
                setSelectedOperationalProviderId(undefined); 
              }}
              placeholder="Todas as prestadoras"
              style={{ width: '100%' }}
              loading={loadingProviders}
              disabled={disabled || loadingProviders}
              allowClear
              showSearch
              options={providers}
              filterOption={(input, option) =>
                (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Column>
        )}

        {}
        {canSeeOperationalProviderFilter && (
          <Column xs={24} sm={12} lg={6}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              Operacional
            </label>
            <Select
              key={`operational-${selectedProviderId || 'no-master'}`}
              value={selectedOperationalProviderId}
              onChange={(value) => {
                setSelectedOperationalProviderId(value);
              }}
              placeholder={
                hasRole([BankRoles.ADMIN_BANK]) && !selectedProviderId
                  ? 'Selecione uma master primeiro'
                  : 'Todas as operacionais'
              }
              style={{ width: '100%' }}
              loading={loadingOperationalProviders}
              disabled={
                disabled ||
                loadingOperationalProviders ||
                (hasRole([BankRoles.ADMIN_BANK]) && !selectedProviderId) ||
                operationalProviders.length === 0
              }
              allowClear
              showSearch
              options={operationalProviders}
              filterOption={(input, option) =>
                (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Column>
        )}

        {}
        <Column xs={24} sm={12} lg={6}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
            Tipo de Inspeção
          </label>
          <Select
            value={selectedInspectionType}
            onChange={(value) => setSelectedInspectionType(value)}
            placeholder="Todos os tipos"
            style={{ width: '100%' }}
            loading={loadingInspectionTypes}
            disabled={disabled || loadingInspectionTypes}
            allowClear
            showSearch
            options={inspectionTypes}
            filterOption={(input, option) =>
              (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Column>

        {}
        <Column xs={24} sm={24} lg={6}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, visibility: 'hidden' }}>
            Ações
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              type="primary"
              icon={<FilterOutlined />}
              onClick={handleFilter}
              disabled={disabled}
              style={{ flex: 1 }}
            >
              Filtrar
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClear}
              disabled={disabled}
            >
              Limpar
            </Button>
          </div>
        </Column>
      </Row>
    </div>
  );
}
